import { ChangeEvent, useId, forwardRef } from "react"; // Import forwardRef
import { Tooltip } from "react-tooltip";

import IconTooltip from "./icon-tooltip.svg?react";

type TooltipType = {
  content?: string;
  onClick?: () => void;
};

type TextareaType = {
  name: string;
  value: string;
  characterLimit?: number;
  placeholder?: string;
  containerClassName?: string;
  className?: string;
  formLabel?: string;
  formSubLabel?: string;
  inputHelpBlock?: string;
  disabled?: boolean;
  isRequired?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  onInputChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  tooltip?: TooltipType;
};

const Textarea = forwardRef<HTMLTextAreaElement, TextareaType>((props, ref) => {
  const {
    name,
    value,
    characterLimit,
    placeholder,
    containerClassName,
    className,
    formLabel,
    formSubLabel,
    inputHelpBlock,
    disabled = false,
    isRequired = false,
    hasError = false,
    errorMessage = "",
    onInputChange,
    tooltip,
  } = props;

  const componentId = useId();

  return (
    <div
      className={`${`theme-form-input-container`} ${
        containerClassName !== undefined ? containerClassName : ""
      }`}
    >
      {formLabel && (
        <label className="theme-form-input-label" htmlFor={`field-${name}`}>
          {formLabel} {isRequired && <span>*</span>}{" "}
          {tooltip && (
            <>
              {tooltip.onClick !== undefined ? (
                <IconTooltip
                  onClick={() => tooltip.onClick && tooltip.onClick()}
                />
              ) : (
                <>
                  <IconTooltip
                    data-tooltip-id={`${componentId}-tooltip`}
                    data-tooltip-html={
                      tooltip.content !== undefined ? tooltip.content : ""
                    }
                  />
                  <Tooltip
                    id={`${componentId}-tooltip`}
                    className="theme-tooltip"
                  />
                </>
              )}
            </>
          )}
        </label>
      )}
      {formSubLabel && (
        <label className="theme-form-sublabel">{formSubLabel}</label>
      )}
      <textarea
        ref={ref} // Assign the 'ref' argument received from forwardRef here
        disabled={disabled}
        placeholder={placeholder}
        name={name}
        id={`field-${name}`}
        value={value !== null ? value : ""}
        className={`theme-form-control theme-form-textarea ${
          className !== undefined ? className : ""
        } ${hasError && "theme-form-control-has-error"}`}
        onChange={(e) => {
          if (characterLimit) {
            if (e.target.value.length > characterLimit) {
              return;
            }
          }
          onInputChange && onInputChange(e);
        }}
      />
      {characterLimit && (
        <span className="theme-textarea-count">
          {/* Use value.length directly */}
          {(value || "").length}/{characterLimit.toString()}
        </span>
      )}
      {hasError && (
        <div className="theme-font-sm color-theme-red-500 theme-form-error-message">
          {errorMessage}
        </div>
      )}
      {inputHelpBlock && (
        <span className="theme-form-helpblock">{inputHelpBlock}</span>
      )}
    </div>
  );
});

// display name for better debugging
Textarea.displayName = "Textarea";

export default Textarea;
